// The admin console's Integrations tab (.design-specs/admin-console.plan.md §5): every sync job by provider and
// health. "Stale" is the scheduler's own definition, served with the panel. Below it, one band's jobs in full —
// including WHY a run that reported "ok" fetched nothing, which no other production surface can show.
import { useState } from "react";
import { Button, Card, DataTable, EmptyState, Picker, Switch, Tag, formatRelativeTime, type DataTableColumn } from "@cntrl/ui";
import { errorMessage } from "../../app/hooks/apiError.js";
import {
  useAdminBandSync,
  useForceAdminSync,
  useAdminBands,
  useAdminIntegrations,
  type AdminAttentionRow,
  type AdminBandSyncRow,
  type AdminProviderHealth,
} from "../../app/hooks/useAdmin.js";
import { ago } from "./adminFormat.js";
import { pushToast } from "../../app/toasts.js";
import styles from "./AdminDashboard.module.scss";

const count = (n: number | undefined) => (n ? n.toLocaleString() : <span className={styles.zero}>0</span>);

/** A count that only matters when it is not zero: tinted, so a healthy table reads as a column of quiet zeros. */
function IssueCount({ n, tone }: { n: number | undefined; tone: "danger" | "warning" | "neutral" }) {
  if (!n) return <span className={styles.zero}>0</span>;
  return (
    <Tag tone={tone} size="sm">
      {n.toLocaleString()}
    </Tag>
  );
}

const PROVIDER_COLUMNS: DataTableColumn<AdminProviderHealth>[] = [
  { key: "provider", header: "Provider", cell: (p) => <strong>{p.provider}</strong> },
  { key: "bands", header: "Bands", align: "end", cell: (p) => count(p.bands) },
  { key: "jobs", header: "Jobs", align: "end", cell: (p) => count(p.jobs) },
  { key: "healthy", header: "Healthy", align: "end", cell: (p) => count(p.healthy) },
  { key: "blocked", header: "Blocked", align: "end", cell: (p) => <IssueCount n={p.blocked} tone="danger" /> },
  { key: "stale", header: "Stale", align: "end", cell: (p) => <IssueCount n={p.stale} tone="danger" /> },
  { key: "failing", header: "Failing", align: "end", cell: (p) => <IssueCount n={p.failing} tone="warning" /> },
  { key: "never", header: "Never landed", align: "end", cell: (p) => <IssueCount n={p.neverLanded} tone="neutral" /> },
  { key: "budget", header: "Out of budget", align: "end", cell: (p) => <IssueCount n={p.budgetExhausted} tone="warning" /> },
  { key: "paused", header: "Paused", align: "end", cell: (p) => <IssueCount n={p.paused} tone="neutral" /> },
];

const ISSUE_TONE: Record<string, "danger" | "warning"> = { blocked: "danger", stale: "danger", failing: "warning" };

const attentionColumns = (onBand: (bandId: string) => void): DataTableColumn<AdminAttentionRow>[] => [
  {
    key: "band",
    header: "Band",
    cell: (a) => (
      <button type="button" className={styles.linkButton} onClick={() => a.bandId && onBand(a.bandId)}>
        {a.bandName}
      </button>
    ),
  },
  { key: "job", header: "Job", cell: (a) => <code className={styles.code}>{`${a.provider} · ${a.kind}`}</code> },
  {
    key: "issue",
    header: "Issue",
    cell: (a) => (
      <Tag tone={ISSUE_TONE[a.issue ?? ""] ?? "neutral"} size="sm">
        {a.issue}
      </Tag>
    ),
  },
  { key: "detail", header: "Detail", cell: (a) => a.detail ?? "—" },
  { key: "since", header: "Since", cell: (a) => formatRelativeTime(a.since) ?? "—" },
  { key: "data", header: "Last data", cell: (a) => formatRelativeTime(a.lastLandOn) ?? "never" },
];

const STATE_TONE: Record<string, "danger" | "warning" | "neutral" | "success"> = {
  blocked: "danger",
  paused: "neutral",
  dispatched: "warning",
  scheduled: "success",
};

const bandSyncColumns = (syncNow: (j: AdminBandSyncRow) => void, pendingKey: string | null): DataTableColumn<AdminBandSyncRow>[] => [
  { key: "job", header: "Job", cell: (j) => <code className={styles.code}>{`${j.provider} · ${j.kind}`}</code> },
  {
    key: "state",
    header: "State",
    cell: (j) => (
      <Tag tone={STATE_TONE[j.state ?? ""] ?? "neutral"} size="sm">
        {j.state}
      </Tag>
    ),
  },
  { key: "status", header: "Last status", cell: (j) => j.lastStatus ?? "—" },
  { key: "ran", header: "Last run", cell: (j) => ago(j.lastRunOn, "never") },
  { key: "data", header: "Last data", cell: (j) => ago(j.lastLandOn, "never") },
  { key: "next", header: "Next run", cell: (j) => ago(j.nextRunOn) },
  {
    key: "why",
    header: "Why",
    // One column for the three reasons a row can carry, in the order they matter: a block will not fix itself,
    // an error is the last failure, a note is why the last SUCCESS fetched nothing.
    cell: (j) =>
      j.blockedReason ? (
        <span className={styles.errorText}>Blocked: {j.blockedReason}</span>
      ) : j.lastError ? (
        <span className={styles.errorText}>
          {j.lastError}
          {(j.consecutiveFailures ?? 0) > 1 && ` (${j.consecutiveFailures} in a row)`}
        </span>
      ) : (
        j.lastNote ?? <span className={styles.zero}>—</span>
      ),
  },
  {
    key: "action",
    header: "Action",
    align: "end",
    // A paused job and one already in flight cannot be forced (the server refuses both), so the button is absent
    // rather than present-and-refused.
    cell: (j) =>
      j.state === "paused" || j.state === "dispatched" ? null : (
        <Button size="sm" variant="ghost" loading={pendingKey === `${j.provider}/${j.kind}`}
          onClick={() => syncNow(j)} aria-label={`Sync ${j.provider} ${j.kind} now`}>
          Sync now
        </Button>
      ),
  },
];

/** One band's sync jobs in full. The band is chosen here or by clicking one under "Needs attention". */
function BandSyncCard({ bandId, onBand }: { bandId: string | undefined; onBand: (bandId: string | undefined) => void }) {
  const bands = useAdminBands();
  const sync = useAdminBandSync(bandId);
  const force = useForceAdminSync(bandId ?? "");
  const [reland, setReland] = useState(false);
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const syncNow = (j: AdminBandSyncRow) => {
    if (!j.provider || !j.kind) return;
    setPendingKey(`${j.provider}/${j.kind}`);
    force.mutate(
      { provider: j.provider, kind: j.kind, reland },
      {
        onSuccess: () => pushToast(`${j.provider} queued. It runs on the scheduler's next tick, within a minute.`, "success"),
        onError: (e) => pushToast(errorMessage(e, `Could not queue ${j.provider}.`)),
        onSettled: () => setPendingKey(null),
      },
    );
  };
  const options = (bands.data ?? [])
    .map((b) => ({ value: b.bandId ?? "", label: b.isFixture ? `${b.name} (fixture)` : (b.name ?? "") }))
    .filter((o) => o.value)
    .sort((a, b) => a.label.localeCompare(b.label));

  return (
    <Card title="Band sync"
      subtitle="Every sync job one band has, and why each last run did what it did. Sync now skips the release window and the minimum interval, never the daily budget.">
      <div className={styles.stack}>
        <div className={styles.controls}>
          <div className={styles.search}>
            <Picker label="Band" hideLabel typeahead placeholder="Choose a band…" value={bandId ?? ""}
              onChange={(v) => onBand(v || undefined)} options={options} />
          </div>
          {bandId && (
            <span className={styles.toggle}>
              <Switch checked={reland} onChange={setReland} label="Re-land even if the answer is unchanged" size="sm" />
              <span aria-hidden="true">Re-land even if unchanged</span>
            </span>
          )}
        </div>
        {!bandId ? null : sync.isLoading ? (
          <p className={styles.zero}>Reading…</p>
        ) : sync.isError ? (
          <p className={styles.error}>{errorMessage(sync.error, "Could not read this band's sync jobs.")}</p>
        ) : (sync.data?.jobs?.length ?? 0) === 0 ? (
          <EmptyState title="No sync jobs" description="This band has connected nothing that syncs." />
        ) : (
          <DataTable columns={bandSyncColumns(syncNow, pendingKey)} rows={sync.data?.jobs ?? []}
            rowKey={(j) => `${j.provider}/${j.kind}`} />
        )}
      </div>
    </Card>
  );
}

export interface IntegrationsTabProps {
  /** The band whose jobs the Band sync card shows (`?band=`), or none. */
  bandId?: string;
  onBand: (bandId: string | undefined) => void;
}

/** Every sync job on the platform by provider and health, plus the jobs that need a person. */
export function IntegrationsTab({ bandId, onBand }: IntegrationsTabProps) {
  return (
    <>
      <PlatformIntegrations onBand={onBand} />
      <BandSyncCard bandId={bandId} onBand={onBand} />
    </>
  );
}

function PlatformIntegrations({ onBand }: { onBand: (bandId: string | undefined) => void }) {
  const q = useAdminIntegrations();

  if (q.isLoading) return <Card title="Integrations" subtitle="Reading every sync job…" />;
  if (q.isError) {
    return (
      <Card title="Integrations">
        <p className={styles.error}>{errorMessage(q.error, "Could not read integrations health.")}</p>
      </Card>
    );
  }
  const panel = q.data;
  if (!panel) return null;

  const jobs = panel.providers?.reduce((n, p) => n + (p.jobs ?? 0), 0) ?? 0;
  const healthy = panel.providers?.reduce((n, p) => n + (p.healthy ?? 0), 0) ?? 0;

  return (
    <Card
      title="Integrations"
      subtitle={`Every sync job on the platform. Stale = no new data for ${panel.staleAfterHours}h. Read ${formatRelativeTime(panel.generatedOnUtc) ?? "just now"}.`}
    >
      <div className={styles.stack}>
        <dl className={styles.headline}>
          <div>
            <dt>Jobs</dt>
            <dd>{jobs.toLocaleString()}</dd>
          </div>
          <div>
            <dt>Healthy</dt>
            <dd>{healthy.toLocaleString()}</dd>
          </div>
          <div>
            <dt>Need attention</dt>
            <dd data-alert={(panel.attentionTotal ?? 0) > 0 || undefined}>{(panel.attentionTotal ?? 0).toLocaleString()}</dd>
          </div>
        </dl>

        {(panel.providers?.length ?? 0) === 0 ? (
          <EmptyState title="No sync jobs" description="No band has connected anything yet." />
        ) : (
          <DataTable columns={PROVIDER_COLUMNS} rows={panel.providers ?? []} rowKey={(p) => p.provider ?? ""} />
        )}

        {(panel.attention?.length ?? 0) > 0 && (
          <>
            <h3 className={styles.subhead}>
              Needs attention
              {(panel.attentionTotal ?? 0) > (panel.attention?.length ?? 0) &&
                ` · worst ${panel.attention?.length} of ${panel.attentionTotal}`}
            </h3>
            <DataTable
              columns={attentionColumns(onBand)}
              rows={panel.attention ?? []}
              rowKey={(a) => `${a.bandId}/${a.provider}/${a.kind}`}
              maxBlockSize="28rem"
            />
          </>
        )}
      </div>
    </Card>
  );
}
